import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  getPendingScholars,
  Scholar,
  approveScholar,
  rejectScholar,
  getRecentActivity,
  getAdvertisements,
} from "../services/adminService";

import { cardStyle, sectionTitle, thStyle } from "../styles/theme";
import { UserPlus, Wallet, LifeBuoy, Settings } from "lucide-react";

const VerificationQueue = () => {
  const [applicants, setApplicants] = useState<Scholar[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchApplicants = async () => {
    setLoading(true);
    const data = await getPendingScholars();
    setApplicants(data);
    setLoading(false);
  };

  useEffect(() => {
    fetchApplicants();
  }, []);

  const handleReview = async (id: string, action: "approve" | "reject") => {
    try {
      if (action === "approve") await approveScholar(id);
      else await rejectScholar(id);
      fetchApplicants(); // refresh list
    } catch (e) {
      console.error(e);
      alert(`Failed to ${action} scholar`);
    }
  };

  // Helper to map religion to color
  const getReligionColor = (religion: string) => {
    const map: Record<string, string> = {
      ISLAM: "#10b981",
      HINDUISM: "#f97316",
      CHRISTIANITY: "#3b82f6",
    };
    return map[religion?.toUpperCase()] || "#64748b";
  };

  return (
    <div style={cardStyle}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "20px",
        }}
      >
        <div>
          <h3 style={{ margin: 0, fontSize: "18px" }}>Verification Queue</h3>
          <p style={{ color: "#64748b", fontSize: "14px", margin: "4px 0 0" }}>
            Pending scholar applications requiring review
          </p>
        </div>
        <button
          style={{
            color: "#2563eb",
            border: "none",
            background: "none",
            fontWeight: "600",
            cursor: "pointer",
          }}
        >
          View All
        </button>
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr
            style={{
              textAlign: "left",
              color: "#94a3b8",
              fontSize: "12px",
              textTransform: "uppercase",
            }}
          >
            <th style={thStyle}>Applicant</th>
            <th style={thStyle}>Religion</th>
            <th style={thStyle}>Date</th>
            <th style={thStyle}>Progress</th>
            <th style={thStyle}>Action</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={5} style={{ padding: "16px", textAlign: "center" }}>
                Loading...
              </td>
            </tr>
          ) : applicants.length === 0 ? (
            <tr>
              <td colSpan={5} style={{ padding: "16px", textAlign: "center" }}>
                No pending applications.
              </td>
            </tr>
          ) : (
            applicants.map((item, i) => {
              const rColor = getReligionColor(item.religion);
              return (
                <tr key={i} style={{ borderTop: "1px solid #f1f5f9" }}>
                  <td
                    style={{
                      padding: "16px 0",
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <div
                      style={{
                        width: "40px",
                        height: "40px",
                        borderRadius: "50%",
                        background: "#eee",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "16px",
                      }}
                    >
                      {item.user?.name?.charAt(0) || "?"}
                    </div>
                    <div>
                      <div style={{ fontSize: "14px", fontWeight: "600" }}>
                        {item.user?.name || "Unknown"}
                      </div>
                      <div style={{ fontSize: "12px", color: "#94a3b8" }}>
                        {item.user?.phone || "No phone"}
                      </div>
                    </div>
                  </td>
                  <td>
                    <span
                      style={{
                        fontSize: "12px",
                        color: rColor,
                        background: `${rColor}15`,
                        padding: "4px 12px",
                        borderRadius: "20px",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <span
                        style={{
                          width: "6px",
                          height: "6px",
                          borderRadius: "50%",
                          background: rColor,
                        }}
                      />{" "}
                      {item.religion}
                    </span>
                  </td>
                  <td style={{ fontSize: "14px", color: "#64748b" }}>
                    {item.status || "Pending"}
                  </td>
                  <td>
                    <span
                      style={{
                        fontSize: "12px",
                        background: "#fef9c3",
                        color: "#854d0e",
                        padding: "4px 12px",
                        borderRadius: "6px",
                        fontWeight: "500",
                      }}
                    >
                      {item.onboardingStep?.replace("_", " ") ||
                        "Action Needed"}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button
                        onClick={() => handleReview(item.id, "approve")}
                        style={{
                          color: "#10b981",
                          border: "1px solid #10b981",
                          background: "none",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          cursor: "pointer",
                        }}
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => handleReview(item.id, "reject")}
                        style={{
                          color: "#ef4444",
                          border: "1px solid #ef4444",
                          background: "none",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          cursor: "pointer",
                        }}
                      >
                        Reject
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

const LiveActivity = () => {
  const [activities, setActivities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchActivity = async () => {
      setLoading(true);
      const data = await getRecentActivity();
      if (data && data.length > 0) {
        setActivities(
          data.map((item: any) => ({
            type: "Booking",
            title: `New Booking: ${item.service?.name || "Service"}`,
            desc: `User ${item.customer?.user?.name || "Unknown"} booked this service.`,
            time: new Date(item.createdAt).toLocaleDateString(),
            color: "#2563eb",
          })),
        );
      } else {
        setActivities([]);
      }
      setLoading(false);
    };
    fetchActivity();
  }, []);

  return (
    <div style={cardStyle}>
      <h3 style={sectionTitle}>Live Activity</h3>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "24px",
          position: "relative",
        }}
      >
        {loading ? (
          <p style={{ fontSize: "12px", color: "#64748b", margin: 0 }}>Loading...</p>
        ) : activities.length === 0 ? (
          <p style={{ fontSize: "12px", color: "#64748b", margin: 0 }}>No recent activity found.</p>
        ) : (
          activities.map((act, i) => (
            <div
              key={i}
              style={{ display: "flex", gap: "16px", position: "relative" }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "50%",
                    background: `${act.color}15`,
                    color: act.color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "14px",
                    zIndex: 2,
                  }}
                >
                  
                </div>
                {i !== activities.length - 1 && (
                  <div
                    style={{
                      width: "2px",
                      flex: 1,
                      background: "#f1f5f9",
                      margin: "4px 0",
                    }}
                  />
                )}
              </div>
              <div>
                <div style={{ fontSize: "14px", fontWeight: "600" }}>
                  {act.title}
                </div>
                <div
                  style={{ fontSize: "12px", color: "#64748b", margin: "4px 0" }}
                >
                  {act.desc}
                </div>
                <div style={{ fontSize: "11px", color: "#94a3b8" }}>
                  {act.time}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      <button
        onClick={() => navigate('/activity-log')}
        style={{
          width: "100%",
          marginTop: "24px",
          padding: "12px",
          borderRadius: "8px",
          border: "1px solid #e2e8f0",
          background: "#fff",
          fontWeight: "600",
          color: "#64748b",
          cursor: "pointer",
        }}
      >
        View Full Activity Log
      </button>
    </div>
  );
};

const AdCampaigns = () => {
  const [ads, setAds] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAds = async () => {
      setLoading(true);
      const data = await getAdvertisements();
      if (data && data.length > 0) {
        setAds(
          data.slice(0, 3).map((ad: any, i: number) => ({
            l: ad.name,
            v: ad.ctr || 0,
            c: ["#10b981", "#f97316", "#2563eb"][i] || "#2563eb",
          })),
        );
      } else {
        setAds([]);
      }
      setLoading(false);
    };
    fetchAds();
  }, []);

  return (
    <div style={cardStyle}>
      <h4
        style={{
          margin: "0 0 16px 0",
          fontSize: "14px",
          color: "#1e293b",
          textTransform: "uppercase",
        }}
      >
        Ad Campaigns
      </h4>
      {loading ? (
        <p style={{ fontSize: "12px", color: "#64748b" }}>Loading...</p>
      ) : ads.length > 0 ? (
        ads.map((ad, i) => (
          <div key={i} style={{ marginBottom: "16px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "13px",
                marginBottom: "8px",
              }}
            >
              <span>{ad.l}</span>
              <span style={{ fontWeight: "bold" }}>{ad.v}% CTR</span>
            </div>
            <div
              style={{
                height: "8px",
                background: "#f1f5f9",
                borderRadius: "4px",
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${Math.min(ad.v * 3, 100)}%`,
                  background: ad.c,
                  borderRadius: "4px",
                }}
              />
            </div>
          </div>
        ))
      ) : (
        <p style={{ fontSize: "12px", color: "#64748b", margin: 0 }}>No active campaigns.</p>
      )}
    </div>
  );
};

const SystemStatus = () => {
  const [status, setStatus] = useState<any[]>([
    { l: "API Server", v: "Checking...", s: "OK" },
    { l: "Database", v: "Checking...", s: "OK" },
    { l: "Email Service", v: "Checking...", s: "OK" },
  ]);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const start = Date.now();
        // Health endpoint lives at the root /health, not under /api/v1
        const baseUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000/api/v1";
        const healthUrl = baseUrl.replace(/\/api\/v1$/, "/health");
        const res = await fetch(healthUrl).catch(() => null);
        if (!res || !res.ok) throw new Error("Server returned non-OK status");
        const latency = Date.now() - start;
        setStatus([
          { l: "API Server", v: "Online", s: "OK" },
          { l: "Database", v: `${latency}ms Latency`, s: "OK" },
          { l: "Email Service", v: "Active", s: "OK" },
        ]);
      } catch (e) {
        setStatus([
          { l: "API Server", v: "Offline", s: "Warn" },
          { l: "Database", v: "Unreachable", s: "Warn" },
          { l: "Email Service", v: "Unknown", s: "Warn" },
        ]);
      }
    };
    checkStatus();
  }, []);

  return (
    <div style={cardStyle}>
      <h4
        style={{
          margin: "0 0 16px 0",
          fontSize: "14px",
          color: "#1e293b",
          textTransform: "uppercase",
        }}
      >
        System Status
      </h4>
      {status.map((sys, i) => (
        <div
          key={i}
          style={{
            padding: "12px",
            borderRadius: "12px",
            background: "#f8fafc",
            marginBottom: "8px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontSize: "13px",
            }}
          >
            <div
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                background: sys.s === "OK" ? "#10b981" : "#f97316",
              }}
            />
            {sys.l}
          </div>
          <span
            style={{ fontSize: "12px", color: "#64748b", fontWeight: "500" }}
          >
            {sys.v}
          </span>
        </div>
      ))}
    </div>
  );
};

const Dashboard1 = () => {
  const navigate = useNavigate();

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      {/* Top Section (Already created in previous step) */}

      {/* Middle Section */}
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "24px" }}>
        <VerificationQueue />
        <LiveActivity />
      </div>

      {/* Bottom Section */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1.5fr 1fr",
          gap: "24px",
        }}
      >
        <AdCampaigns />

        <div style={cardStyle}>
          <h4
            style={{
              margin: "0 0 16px 0",
              fontSize: "14px",
              color: "#1e293b",
              textTransform: "uppercase",
            }}
          >
            Quick Actions
          </h4>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "12px",
            }}
          >
            {[
              { n: "Add Scholar", icon: UserPlus, color: "#f59e0b", bg: "rgba(245,158,11,0.1)", route: "/scholars" },
              { n: "Process Payouts", icon: Wallet, color: "#10b981", bg: "rgba(16,185,129,0.1)", route: "/finance/payouts" },
              { n: "Support Tickets", icon: LifeBuoy, color: "#2563eb", bg: "rgba(37,99,235,0.1)", route: "/support" },
              { n: "Sys Config", icon: Settings, color: "#64748b", bg: "rgba(100,116,139,0.1)", route: "/settings" },
            ].map((btn, i) => {
              const Icon = btn.icon;
              return (
                <button
                  key={i}
                  onClick={() => navigate(btn.route)}
                  style={{
                    padding: "16px 12px",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "12px",
                    background: "hsl(var(--card))",
                    textAlign: "center",
                    cursor: "pointer",
                    transition: "all 0.2s ease-in-out",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <div
                    style={{
                      width: 38,
                      height: 38,
                      borderRadius: 10,
                      backgroundColor: btn.bg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: btn.color,
                    }}
                  >
                    <Icon size={18} />
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      fontWeight: "700",
                      color: "hsl(var(--foreground))",
                    }}
                  >
                    {btn.n}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <SystemStatus />
      </div>
    </div>
  );
};

export default Dashboard1;
